import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params';

import { Icon, Tooltip, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import { Attachment } from '../../common/attachment/Attachment.interface';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';
import { pluralize } from '../../common/formatter';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';
import StatusLabel from '../../common/StatusLabel';
import VerticalStep from '../../common/vertical-stepper/VerticalStep';
import VerticalStepper from '../../common/vertical-stepper/VerticalStepper';
import ElectronicSignatureDialog from '../electronic-signature/ElectronicSignatureDialog';
import TrainingLessonCard from './cards/TrainingLessonCard';
import { useFindTrainingVersion } from './Training.hooks';
import {
  TrainingSectionAttachmentProgress,
  TrainingUserAttachment,
  TrainingUserStatus,
  TrainingVersion
} from './Training.interface';
import {
  useGetTrainingUser,
  useUpdateTrainingUser
} from './TrainingUser.hooks';

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  margin: 0 -30px;

  .warning-icon {
    color: ${props => props.theme.palette.warning.main};
  }
`;

const StyledContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  .alert-view-mode {
    width: 100%;
    justify-content: center;
  }
`;

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  .training-id {
    line-height: unset;
  }
  .status-container {
    display: flex;
    margin-top: 5px;
    .status-label {
      margin-right: 8px;
    }
  }
`;

const TrainingLessonPage: React.FC = () => {
  const history = useHistory();
  const { trainingId = 0 } = useParams<{ trainingId?: string }>();
  const { trainingVersionId = 0 } = useParams<{ trainingVersionId?: string }>();
  const { userId = 0 } = useParams<{ userId?: string }>();
  const [isViewOnly, setIsViewOnly] = useQueryParam(
    'view-only',
    withDefault(BooleanParam, false)
  );
  const [trainingVersion, setTrainingVersion] =
    useState<Partial<TrainingVersion>>();
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const [openSignatureDialog, setOpenSignatureDialog] = useState(false);
  const [openStartDialog, setOpenStartDialog] = useState(false);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const {
    trainingUser: foundTrainingUser,
    isLoading: isLoadingTrainingUser,
    getTrainingUser
  } = useGetTrainingUser(+trainingId, +trainingVersionId, +userId);
  const [trainingUser, setTrainingUser] = useState(foundTrainingUser);
  const {
    trainingVersion: loadedTrainingVersion,
    isLoading: isLoadingTrainingVersion,
    findTrainingVersion
  } = useFindTrainingVersion(+trainingId, +trainingVersionId);

  // get training user if userId is provided
  useEffect(() => {
    if (userId) {
      getTrainingUser();
    } else {
      setIsViewOnly(true);
      findTrainingVersion();
    }
  }, [findTrainingVersion, getTrainingUser, setIsViewOnly, userId]);
  // set training version state
  useEffect(() => {
    if (loadedTrainingVersion) {
      setTrainingVersion(loadedTrainingVersion);
    } else if (foundTrainingUser) {
      setTrainingVersion(foundTrainingUser.trainingVersion);
    }
  }, [loadedTrainingVersion, foundTrainingUser]);
  // update training user
  const { trainingUser: updatedTrainingUser, patch: updateTrainingUser } =
    useUpdateTrainingUser(+trainingId, +trainingVersionId, +userId);
  const isLoading = isLoadingTrainingUser || isLoadingTrainingVersion;

  useEffect(() => {
    setTrainingUser(foundTrainingUser);
  }, [foundTrainingUser]);

  useEffect(() => {
    if (
      !isViewOnly &&
      foundTrainingUser &&
      foundTrainingUser.status === TrainingUserStatus.ToDo
    ) {
      setOpenStartDialog(true);
    }
  }, [isViewOnly, foundTrainingUser]);

  useEffect(() => {
    if (updatedTrainingUser) {
      setTrainingUser(updatedTrainingUser);
      if (updatedTrainingUser.status === TrainingUserStatus.Completed) {
        history.push('/portal/training');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTrainingUser]);

  const handlePrevious = () => {
    setSelectedStepIndex(selectedStepIndex - 1);
  };

  const handleNext = () => {
    if (selectedStepIndex < sections.length - 1) {
      setSelectedStepIndex(selectedStepIndex + 1);
    } else {
      const hasIncompleteSection = sectionAttachmentsProgress.some(progress => {
        return !progress.isValid;
      });
      if (hasIncompleteSection) {
        setOpenWarningDialog(true);
      } else {
        setOpenSignatureDialog(true);
      }
    }
  };

  const sections = trainingVersion?.sections || [];
  const version = trainingVersion?.version;
  const section = sections[selectedStepIndex];

  const sectionAttachments = useMemo(() => {
    return (trainingUser?.attachments || []).filter(
      attachment => attachment.sectionIndex === selectedStepIndex
    );
  }, [trainingUser, selectedStepIndex]);

  const sectionAttachmentsProgress = useMemo(() => {
    const sectionAttachmentProgress: TrainingSectionAttachmentProgress[] = [];

    for (let index = 0; index < sections.length; index++) {
      const section = sections[index];
      // check if this section is file upload enabled and with minimum file uploads
      if (
        !(
          section.fileUpload &&
          section.fileUpload.enabled &&
          typeof section.fileUpload.minRequired === 'number' &&
          section.fileUpload.minRequired > 0
        )
      ) {
        continue;
      }
      const min = section.fileUpload.minRequired;
      const total = (trainingUser?.attachments || []).filter(attachments => {
        return attachments.sectionIndex === index;
      }).length;
      const isValid = total >= min;
      sectionAttachmentProgress.push({ index, total, min, isValid });
    }
    return sectionAttachmentProgress;
  }, [sections, trainingUser]);

  const getAttachmentProgress = useCallback(
    (index: number) => {
      return sectionAttachmentsProgress.find(
        progress => progress.index === index
      );
    },
    [sectionAttachmentsProgress]
  );

  const onChangeAttachments = useCallback(
    (attachments: Attachment[]) => {
      setTrainingUser(trainingUser => {
        if (!trainingUser) return;
        const copy = { ...trainingUser };
        const filteredAttachments = copy.attachments.filter(
          attachment => attachment.sectionIndex !== selectedStepIndex
        );
        const trainingUserAttachments = attachments.map(
          (attachment): TrainingUserAttachment => {
            return { ...attachment, sectionIndex: selectedStepIndex };
          }
        );
        filteredAttachments.push(...trainingUserAttachments);
        copy.attachments = filteredAttachments;
        return copy;
      });
    },
    [selectedStepIndex]
  );

  return (
    <PageTemplate>
      <PageTemplateHeader>
        {trainingVersion && (
          <StyledTitle>
            <Typography variant='overline' className='training-id'>
              TRAINING-{trainingVersion.trainingId}
            </Typography>
            <h2>{trainingVersion.name}</h2>
            <div className='status-container'>
              <StatusLabel status={`Version ${version}`} color='approved' />
            </div>
          </StyledTitle>
        )}
      </PageTemplateHeader>
      <StyledContainer>
        <VerticalStepper>
          {sections.map((step, index) => {
            const attachmentProgress = getAttachmentProgress(index);

            return (
              <VerticalStep
                key={index}
                label={step.name}
                step={index + 1}
                completed={index < selectedStepIndex}
                selected={index === selectedStepIndex}
                onClick={e => setSelectedStepIndex(index)}
                endAdornment={
                  attachmentProgress &&
                  !attachmentProgress.isValid && (
                    <Tooltip
                      arrow
                      placement='top'
                      title={
                        <>
                          Uploaded {attachmentProgress.total} of{' '}
                          {attachmentProgress.min} required{' '}
                          {pluralize(attachmentProgress.min, 'file', 'files')}.
                        </>
                      }
                    >
                      <Icon className='warning-icon'>warning-icon</Icon>
                    </Tooltip>
                  )
                }
              />
            );
          })}
        </VerticalStepper>
        <StyledContent>
          {isViewOnly && (
            <Alert severity='warning' className='alert-view-mode'>
              Training view mode: this training session is not being tracked.
            </Alert>
          )}
          <TrainingLessonCard
            isLoading={isLoading}
            attachments={sectionAttachments}
            attachmentProgress={getAttachmentProgress(selectedStepIndex)}
            section={section}
            onPrevious={selectedStepIndex > 0 ? handlePrevious : undefined}
            onNext={handleNext}
            onChangeAttachments={onChangeAttachments}
            isViewOnly={
              trainingUser?.status === TrainingUserStatus.Completed ||
              isViewOnly
            }
          />
        </StyledContent>
      </StyledContainer>
      {openStartDialog && trainingUser?.status === TrainingUserStatus.ToDo && (
        <ConfirmDialog
          open={openStartDialog}
          title={`Start ${trainingVersion?.name}`}
          message='Would you like to start the training now?'
          onCancel={() => {
            setOpenStartDialog(false);
            history.push('/portal/training'); // back to training dashboard
          }}
          onConfirm={() => {
            setOpenStartDialog(false);
            updateTrainingUser({
              status: TrainingUserStatus.InProgress,
              attachments: trainingUser.attachments
            });
          }}
        />
      )}
      <ConfirmDialog
        open={openWarningDialog}
        title='File Uploads Required'
        message={
          <Alert severity='warning'>
            Some of the sections or steps require a minimum number of file
            uploads.
            <ul style={{ paddingLeft: 13 }}>
              {sectionAttachmentsProgress
                .filter(progress => !progress.isValid)
                .map((progress, index) => (
                  <li key={index}>
                    <strong>{sections[progress.index].name}</strong>: Uploaded{' '}
                    <strong>{progress.total}</strong> of{' '}
                    <strong>{progress.min}</strong> required{' '}
                    {pluralize(progress.min, 'file', 'files')}.
                  </li>
                ))}
            </ul>
          </Alert>
        }
        onCancel={() => setOpenWarningDialog(false)}
        onConfirm={() => setOpenWarningDialog(false)}
      />
      {openSignatureDialog && (
        <ElectronicSignatureDialog
          open={openSignatureDialog}
          meaning='Electronically sign that you have reviewed the document.'
          onCancel={() => setOpenSignatureDialog(false)}
          onSigned={() => {
            setOpenSignatureDialog(false);
            updateTrainingUser({
              status: TrainingUserStatus.Completed,
              attachments: trainingUser?.attachments
            });
          }}
        />
      )}
    </PageTemplate>
  );
};

export default TrainingLessonPage;
